import { Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import {
    isLocalProjectReferencePhoto,
    isRemoteProjectReferencePhoto,
    ProjectReferencePhoto,
    ProjectReferencePhotoType
} from '../../../models/project-reference-photo';
import {BehaviorSubject} from 'rxjs';
import {ProjectReferencePhotoService} from '../../../services/project-reference-photo.service';
import {ToastrService} from 'ngx-toastr';
import {VwuiModalService} from '@bvgo-ict/vwui-angular';
import {FocusPointModalComponent} from '../../focus-point-modal/focus-point-modal.component';
import {PhotoFocus} from '../../../models/photo-focus';

@Component({
    selector: 'app-prd-photo',
    templateUrl: './prd-photo.component.html',
    styleUrls: ['./prd-photo.component.scss'],
    standalone: false
})
export class PrdPhotoComponent implements OnChanges {
    private projectReferencePhotoService = inject<ProjectReferencePhotoService>('ProjectReferencePhotoService' as any);
    private vwuiModalService = inject(VwuiModalService);
    private toast = inject(ToastrService);

    @Input() projectReferenceId?: number;
    @Input() projectReferencePhoto!: ProjectReferencePhotoType;
    @Input() readonly: boolean | null = false;
    @Input() staticThumbnail: boolean = false;

    @Output() photoCreated = new EventEmitter<void>();
    @Output() photoDeleted = new EventEmitter<void>();
    @Output() photoUpdated = new EventEmitter<void>();
    @Output() photoUploadFailed = new EventEmitter();

    photo$ = new BehaviorSubject<ProjectReferencePhoto & {width?: number, height?: number} | null>(null);
    localPreviewSrc$ = new BehaviorSubject<string | ArrayBuffer | null>(null);
    imageType$ = new BehaviorSubject<'local' | 'photo' | null>(null);
    primary$ = new BehaviorSubject(false);
    loading$ = new BehaviorSubject(false);

    ngOnChanges(): void {
        if (this.projectReferencePhoto && this.projectReferenceId) {
            if (isLocalProjectReferencePhoto(this.projectReferencePhoto)) {
                this.imageType$.next('local');
                this.createPhoto(this.projectReferenceId, this.projectReferencePhoto.localFile);
            } else {
                this.imageType$.next('photo');
                this.photo$.next(this.projectReferencePhoto);
                this.primary$.next(this.projectReferencePhoto.primary);
            }
        }
    }

    private async createPhoto(projectReferenceId: number, file: File): Promise<void> {
        this.loading$.next(true);

        this.readLocalImageData(file);

        try {
            await this.postPhoto(projectReferenceId, file);

            this.photoCreated.next();
        } catch (e) {
            this.toast.error('Afbeelding uploaden mislukt. Probeer het opnieuw.');
            this.photoUploadFailed.next();
        } finally {
            this.loading$.next(false);
        }
    }

    async deletePhoto(): Promise<void> {
        if (isRemoteProjectReferencePhoto(this.projectReferencePhoto)) {
            await this.projectReferencePhotoService.deletePhoto(this.projectReferencePhoto.id).toPromise();

            this.photoDeleted.next();
        }
    }

    async setPrimaryPhoto(): Promise<void> {
        if (isRemoteProjectReferencePhoto(this.projectReferencePhoto) && this.projectReferenceId) {
            await this.projectReferencePhotoService.putPrimaryPhoto(this.projectReferenceId, this.projectReferencePhoto.id).toPromise();

            this.photoUpdated.next();
        }
    }

    setFocusPoint() {
        if (isRemoteProjectReferencePhoto(this.projectReferencePhoto)) {
            this.vwuiModalService.open(FocusPointModalComponent, {
                data: {projectReferencePhoto: this.projectReferencePhoto},
                modalClass: 'vwui-modal--wide',
                closeOnBackdropClick: false
            }).afterClosed.subscribe(async focus => {
                if (focus && isRemoteProjectReferencePhoto(this.projectReferencePhoto)) {
                    this.saveFocusPoint(this.projectReferencePhoto, focus);
                }
            });
        }
    }

    private async saveFocusPoint(projectReferencePhoto: ProjectReferencePhoto, focus: PhotoFocus) {
        try {
            await this.projectReferencePhotoService.putFocusPoint(projectReferencePhoto.id, focus).toPromise();
            this.toast.success('Het focuspunt is aangepast.');
        } catch (error) {
            this.toast.error('Focuspunt opslaan is mislukt.');
        }

        this.photoUpdated.emit();
    }

    private readLocalImageData(file: File): void {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            this.localPreviewSrc$.next(fileReader.result);
        };

        fileReader.readAsDataURL(file);
    }

    private async postPhoto(projectReferenceId: number, file: File): Promise<void> {
        try {
            return await this.projectReferencePhotoService.postPhoto(projectReferenceId, file).toPromise();
        } catch (e) {
            console.error('uploadFileToBlob error', e);
            throw e;
        }
    }
}
